import { logger } from '@/lib/logger';

const PROGRESS_STORAGE_KEY = 'dyslexa_progress_state';

// IndexedDB utility class
class IndexedDBStorage {
    private static DB_NAME = 'DyslexaProgressDB';
    private static DB_VERSION = 1;
    private static STORE_NAME = 'progressState';
    private static OPEN_TIMEOUT_MS = 3000;

    private static async openDB(): Promise<IDBDatabase | null> {
        if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
            return null;
        }

        return new Promise((resolve) => {
            let settled = false;
            const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);
            const timeout = window.setTimeout(() => {
                if (!settled) {
                    settled = true;
                    console.warn('IndexedDB open timeout');
                    resolve(null);
                }
            }, this.OPEN_TIMEOUT_MS);

            request.onerror = () => {
                if (!settled) {
                    settled = true;
                    window.clearTimeout(timeout);
                    console.warn('IndexedDB open failed', request.error);
                    resolve(null);
                }
            };

            request.onsuccess = () => {
                if (!settled) {
                    settled = true;
                    window.clearTimeout(timeout);
                    resolve(request.result);
                } else {
                    // Timeout already fired; close the orphaned connection
                    request.result.close();
                }
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(this.STORE_NAME)) {
                    db.createObjectStore(this.STORE_NAME);
                }
            };
        });
    }

    static async setItem(key: string, value: string): Promise<void> {
        const db = await this.openDB();
        if (!db) {
            throw new Error('IndexedDB unavailable');
        }

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.STORE_NAME], 'readwrite');
            const store = transaction.objectStore(this.STORE_NAME);
            
            store.put(value, key);
            
            transaction.oncomplete = () => {
                db.close();
                resolve();
            };
            transaction.onerror = () => {
                db.close();
                reject(transaction.error);
            };
            transaction.onabort = () => {
                db.close();
                reject(new Error('Transaction aborted'));
            };
        });
    }

    static async getItem(key: string): Promise<string | null> {
        const db = await this.openDB();
        if (!db) {
            return null;
        }

        return new Promise((resolve) => {
            try {
                const transaction = db.transaction([this.STORE_NAME], 'readonly');
                const store = transaction.objectStore(this.STORE_NAME);
                const request = store.get(key);
                
                request.onsuccess = () => {
                    db.close();
                    resolve(request.result || null);
                };
                request.onerror = () => {
                    db.close();
                    console.warn('Failed to get IndexedDB item', request.error);
                    resolve(null);
                };
                transaction.onerror = () => {
                    db.close();
                    console.warn('Failed to get IndexedDB item (transaction error)', transaction.error);
                    resolve(null);
                };
                transaction.onabort = () => {
                    db.close();
                    console.warn('Failed to get IndexedDB item (transaction aborted)');
                    resolve(null);
                };
            } catch (error) {
                db.close();
                console.warn('Failed to get IndexedDB item', error);
                resolve(null);
            }
        });
    }

    static async removeItem(key: string): Promise<void> {
        const db = await this.openDB();
        if (!db) {
            return;
        }

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction([this.STORE_NAME], 'readwrite');
                const store = transaction.objectStore(this.STORE_NAME);
                
                store.delete(key);
                
                transaction.oncomplete = () => {
                    db.close();
                    resolve();
                };
                transaction.onerror = () => {
                    db.close();
                    reject(transaction.error);
                };
                transaction.onabort = () => {
                    db.close();
                    reject(new Error('Transaction aborted'));
                };
            } catch (error) {
                db.close();
                reject(error);
            }
        });
    }

    static async clear(): Promise<void> {
        const db = await this.openDB();
        if (!db) {
            return;
        }

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction([this.STORE_NAME], 'readwrite');
                const store = transaction.objectStore(this.STORE_NAME);
                
                store.clear();
                
                transaction.oncomplete = () => {
                    db.close();
                    resolve();
                };
                transaction.onerror = () => {
                    db.close();
                    reject(transaction.error);
                };
                transaction.onabort = () => {
                    db.close();
                    reject(new Error('Transaction aborted'));
                };
            } catch (error) {
                db.close();
                reject(error);
            }
        });
    }
}

// Storage format (what gets saved to IndexedDB)
interface ProcessingProgressStateStorage {
    currentStep: number | undefined
    totalSteps: number | undefined
    fileNames: string[]  // File names for reference
    fileData: string[]   // Base64 encoded file data
    fileTypes: string[]  // MIME types
    processedText?: string  // Accumulated processed text results
}

// In-memory format (what the class uses)
export interface ProcessingProgressStateData {
    currentStep: number | undefined
    totalSteps: number | undefined
    files: File[]
    processedText?: string  // Accumulated processed text results
}

export class ProcessingProgressState {
    private currentStep: number | undefined = undefined
    private totalSteps: number | undefined = undefined
    private files: File[] = []
    private processedText: string | undefined = undefined

    private static async setStorageItem(data: string): Promise<boolean> {
        if (typeof window !== 'undefined') {
            try {
                const chunks = ProcessingProgressState.splitStringIntoChunks(data);
                
                // Clear existing chunks first
                await ProcessingProgressState.removeStorageItem();
                
                // Store each chunk
                for (let index = 0; index < chunks.length; index++) {
                    const chunk: string = chunks[index];
                    await IndexedDBStorage.setItem(PROGRESS_STORAGE_KEY + "_" + index, chunk);
                    // sessionStorage.setItem(PROGRESS_STORAGE_KEY + "_" + index, chunk);
                }
                return true;
            } catch (e) {
                console.warn('Failed to set storage item', e);
                const message = (e instanceof Error ? e.message : String(e)).toLowerCase();
                if (
                    message.includes('quota') ||
                    message.includes('storage') ||
                    message.includes('indexeddb unavailable')
                ) {
                    // Continue without persistent resume support.
                    return false;
                }
                return false;
            }
        }
        return false;
    }

    private static async getStorageItem(): Promise<string | null> {
        if (typeof window !== 'undefined') {
            try {
                let data = "";
                for (let index = 0; ; index++) {
                    const chunk = await IndexedDBStorage.getItem(PROGRESS_STORAGE_KEY + "_" + index);
                    // const chunk = sessionStorage.getItem(PROGRESS_STORAGE_KEY + "_" + index);
                    if (chunk === null) break;
                    data += chunk;
                }
                return data || null;
            } catch (e) {
                console.warn('Failed to get storage item', e);
                return null;
            }
        }
        return null;
    }

    private static async removeStorageItem(): Promise<void> {
        if (typeof window !== 'undefined') {
            try {
                for (let index = 0; ; index++) {
                    const chunk = await IndexedDBStorage.getItem(PROGRESS_STORAGE_KEY + "_" + index);
                    // const chunk = sessionStorage.getItem(PROGRESS_STORAGE_KEY + "_" + index);
                    if (chunk === null) break;
                    await IndexedDBStorage.removeItem(PROGRESS_STORAGE_KEY + "_" + index);
                    //  sessionStorage.removeItem(PROGRESS_STORAGE_KEY + "_" + index);
                }
            } catch (e) {
                console.warn('Failed to remove storage item', e);
            }
        }
    }

    private static splitStringIntoChunks(str: string, chunkSize: number = 512 * 512): string[] {
        if (chunkSize <= 0) {
            throw new Error('Chunk size must be positive');
        }
        
        if (str.length === 0) {
            return [];
        }
        
        const chunks: string[] = [];
        
        for (let i = 0; i < str.length; i += chunkSize) {
            const chunk: string = str.substring(i, i + chunkSize);
            chunks.push(chunk);
        }
        
        return chunks;
    }

    constructor(state?: ProcessingProgressStateData) {
        if (state) {
            this.currentStep = state.currentStep
            this.totalSteps = state.totalSteps
            this.files = state.files || []
            this.processedText = state.processedText
        }
    }

    public getState(): ProcessingProgressStateData {
        return {
            currentStep: this.currentStep,
            totalSteps: this.totalSteps,
            files: [...this.files],  // Return a copy
            processedText: this.processedText
        }
    }

    public setCurrentStep(step: number | undefined, processedText?: string): void {
        this.currentStep = step
        if (processedText !== undefined) {
            this.processedText = processedText
        }
    }

    public setTotalSteps(steps: number | undefined): void {
        this.totalSteps = steps
    }

    public setFiles(files: File[]): void {
        this.files = [...files]  // Store a copy
    }


    public getCurrentStep(): number | undefined {
        return this.currentStep
    }

    public getTotalSteps(): number | undefined {
        return this.totalSteps
    }

    public getFiles(): File[] {
        return [...this.files]  // Return a copy
    }

    public getProcessedText(): string | undefined {
        return this.processedText
    }

    public setProcessedText(text: string | undefined): void {
        this.processedText = text
    }


    public isActive(): boolean {
        return this.currentStep !== undefined && this.totalSteps !== undefined
    }

    // Convert File to base64
    private static async fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                // Remove data URL prefix (e.g., "data:application/pdf;base64,")
                const base64 = result.split(',')[1] || result;
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    // Convert base64 back to File
    private static base64ToFile(base64: string, filename: string, mimeType: string): File {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return new File([bytes], filename, { type: mimeType });
    }

    // Save to storage - convert Files to base64
    public async saveToStorage(): Promise<void> {
        try {
            if (this.isActive()) {
                // Convert files to base64
                const fileDataPromises = this.files.map(file => 
                    ProcessingProgressState.fileToBase64(file)
                );
                const fileData = await Promise.all(fileDataPromises);
                const fileNames = this.files.map(file => file.name);
                const fileTypes = this.files.map(file => file.type);

                const storageData: ProcessingProgressStateStorage = {
                    currentStep: this.currentStep,
                    totalSteps: this.totalSteps,
                    fileNames: fileNames,
                    fileData: fileData,
                    fileTypes: fileTypes,
                    processedText: this.processedText
                };

                const stored = await ProcessingProgressState.setStorageItem(JSON.stringify(storageData));
                if (!stored) {
                    /*
                     * Through the logger rather than the console, because this is the
                     * moment the resume record stops existing and nothing else says so.
                     * A console warning never reaches a bug report, so a job that
                     * quietly stopped persisting read exactly like one that did not -
                     * and the resume that then failed to happen looked like a bug
                     * somewhere else entirely. DYS-040.
                     */
                    logger.warn('Progress state persistence unavailable; continuing in-memory', {
                        currentStep: this.currentStep,
                        totalSteps: this.totalSteps,
                        fileCount: this.files.length,
                    });
                }
            } else {
                await ProcessingProgressState.removeStorageItem();
            }
        } catch (e) {
            logger.warn('Failed to save progress state to storage', {
                error: e instanceof Error ? e.message : String(e),
                currentStep: this.currentStep,
                totalSteps: this.totalSteps,
                fileCount: this.files.length,
            });
        }
    }

    // Load from storage - convert base64 back to Files
    public static async loadFromStorage(): Promise<ProcessingProgressStateData | null> {
        try {
            const saved = await ProcessingProgressState.getStorageItem();
            if (!saved) return null;

            const storageData: ProcessingProgressStateStorage = JSON.parse(saved);

            // Convert base64 back to Files
            const files: File[] = [];
            if (storageData.fileData && storageData.fileNames && storageData.fileTypes) {
                for (let i = 0; i < storageData.fileData.length; i++) {
                    const file = ProcessingProgressState.base64ToFile(
                        storageData.fileData[i],
                        storageData.fileNames[i],
                        storageData.fileTypes[i]
                    );
                    files.push(file);
                }
            }

            return {
                currentStep: storageData.currentStep,
                totalSteps: storageData.totalSteps,
                files: files,
                processedText: storageData.processedText
            };
        } catch (e) {
            logger.warn('Failed to load progress state from storage', {
                error: e instanceof Error ? e.message : String(e),
            });
            return null;
        }
    }

    public async clearStorage(): Promise<void> {
        await ProcessingProgressState.removeStorageItem();
    }

    public async reset(): Promise<void> {
        this.currentStep = undefined
        this.totalSteps = undefined
        this.files = []
        this.processedText = undefined
    }
}

export class ProcessingScreenProgressManager {
    private state: ProcessingProgressState
    private changeCallback?: (state: ProcessingProgressStateData) => void

    constructor() {
        // Initialize with empty state - restore from storage will happen on client side
        this.state = new ProcessingProgressState(undefined)
    }

    public async restoreFromStorage(): Promise<void> {
        // Only restore from storage on client side after mount
        // Only restore if current state is empty (not active) to avoid overwriting active processing
        if (typeof window !== 'undefined' && !this.isActive()) {
            const savedState = await ProcessingProgressState.loadFromStorage();
            if (savedState && savedState.currentStep !== undefined && savedState.totalSteps !== undefined) {
                this.state = new ProcessingProgressState(savedState);
                await this.notifyChange();
            }
        }
    }

    public getState(): ProcessingProgressStateData {
        return this.state.getState()
    }

    public async initialize(totalSteps: number, files: File[] = []): Promise<void> {
        this.state.setCurrentStep(0)
        this.state.setTotalSteps(totalSteps)
        this.state.setFiles(files)
        await this.notifyChange()
    }

    public async setStep(step: number, processedText?: string): Promise<void> {
        this.state.setCurrentStep(step, processedText)
        await this.notifyChange()
    }

    public async nextStep(): Promise<void> {
        const currentStep = this.state.getCurrentStep()
        if (currentStep !== undefined) {
            this.state.setCurrentStep(currentStep + 1)
            await this.notifyChange()
        }
    }

    public async reset(): Promise<void> {
        await this.state.reset()
        await this.state.clearStorage()
        await this.notifyChange()
    }

    public isActive(): boolean {
        return this.state.isActive()
    }

    public getProgress(): number {
        if (!this.isActive()) return 0
        const currentStep = this.state.getCurrentStep()!
        const totalSteps = this.state.getTotalSteps()!
        if (totalSteps === 0) return 0
        return Math.floor((currentStep / totalSteps) * 100)
    }

    public getProcessedText(): string | undefined {
        return this.state.getProcessedText()
    }


    public onChange(callback: (state: ProcessingProgressStateData) => void): void {
        this.changeCallback = callback
    }

    private async notifyChange(): Promise<void> {
        await this.state.saveToStorage()
        if (this.changeCallback) {
            this.changeCallback(this.state.getState())
        }
    }
}

// Note: The class ProcessingProgressState handles state management operations
// The interface ProcessingProgressStateData represents the state data structure
// For backward compatibility, import ProcessingProgressStateData and use it as needed